<template>
    <div>
        <!-- Filter UI (only shown when filters are available) -->
        <div
            v-if="showFilters"
            class="md:flex mb-8"
        >
            <div class="md:w-1/5 md:pr-4">
                <div class="mb-8">
                    <!-- Mobile filter button -->
                    <div class="-mx-4 md:mx-0 md:hidden bg-pink-100 font-heading text-lg">
                        <div
                            class="flex items-center justify-center gap-2 py-2"
                            @click="showFilteringDrawer = true"
                        >
                            <FilterIcon class="w-4 h-4 text-grey-darkest" />
                            <span>
                                Filter
                            </span>
                        </div>
                    </div>

                    <!-- Mobile filter drawer -->
                    <Drawer
                        :open="showFilteringDrawer"
                        title="Filter Results"
                        position="left"
                        @close="showFilteringDrawer = false"
                    >
                        <CategoryFilters
                            :category="parent"
                            :filter-by="filterBy"
                            :tag-ids="tagIds"
                            :attribute-ids="attributeIds"
                            @change:filter-by="handleFilterByChange"
                            @change:tag-ids="handleTagIdsChange"
                            @change:attribute-ids="handleAttributeIdsChange"
                        />
                    </Drawer>

                    <!-- Desktop filters -->
                    <div class="hidden md:block">
                        <CategoryFilters
                            :category="parent"
                            :filter-by="filterBy"
                            :tag-ids="tagIds"
                            :attribute-ids="attributeIds"
                            @change:filter-by="handleFilterByChange"
                            @change:tag-ids="handleTagIdsChange"
                            @change:attribute-ids="handleAttributeIdsChange"
                        />
                    </div>
                </div>
            </div>

            <div class="md:w-full">
                <!-- Loading state -->
                <div
                    v-show="isLoading"
                    class="grid grid-cols-2 gap-4 gap-y-8 md:gap-8"
                    :class="`md:grid-cols-${parent.columns || 3}`"
                >
                    <CatalogueTilePlaceholder
                        v-for="i in 6"
                        :key="i"
                    />
                </div>

                <!-- Category grid or empty state -->
                <div v-show="!isLoading">
                    <div
                        v-if="displayedChildren.length > 0"
                        class="grid grid-cols-2 gap-4 gap-y-8 md:gap-8"
                        :class="`md:grid-cols-${parent.columns || 3}`"
                    >
                        <CategoryCard
                            v-for="(category, index) in displayedChildren"
                            :key="index"
                            :category="category"
                        />
                    </div>

                    <!-- Empty state when filters are active but no results -->
                    <div
                        v-else-if="hasActiveFilters"
                        class="flex flex-col justify-center items-center text-grey-darker bg-grey-lighter rounded-lg h-48 font-semibold"
                    >
                        <EmojiSadIcon class="w-16 h-16" />
                        <div class="text-2xl mt-5">
                            No themes match your selected filters
                        </div>
                        <div class="text-sm mt-2">
                            Try adjusting your filters or browse all themes
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Simple grid (when no filters available) -->
        <div
            v-else
            class="grid grid-cols-2 gap-4 gap-y-8 md:gap-8"
            :class="`md:grid-cols-${parent.columns || 3}`"
        >
            <CategoryCard
                v-for="(category, index) in children"
                :key="index"
                :category="category"
            />
        </div>
    </div>
</template>

<script>
import debounce from 'debounce';
import CategoryCard from '@/components/CategoryCard';
import CategoryFilters from '@/components/CategoryFilters';
import Drawer from '@/components/Drawer';
import FilterIcon from '~/static/fonts/LineIcons/filter.svg';
import EmojiSadIcon from '~/static/fonts/LineIcons/emoji-sad.svg';
import CatalogueTilePlaceholder from '@/components/CatalogueTilePlaceholder';

export default {

    components: {
        CategoryCard,
        CategoryFilters,
        Drawer,
        FilterIcon,
        EmojiSadIcon,
        CatalogueTilePlaceholder,
    },

    props: {
        parent: {
            type: Object,
            required: true,
        },
        children: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            filterBy: 'all',
            tagIds: [],
            attributeIds: [],
            filteredChildren: this.children,
            showFilteringDrawer: false,
            isLoading: false,
        };
    },

    computed: {
        showFilters() {
            return this.parent.display_filters
                || (this.parent.tags && this.parent.tags.length > 0)
                || (this.parent.attributes && this.parent.attributes.length > 0);
        },
        displayedChildren() {
            return this.filteredChildren;
        },
        hasActiveFilters() {
            return this.filterBy !== 'all'
                || this.tagIds.length > 0
                || this.attributeIds.length > 0;
        },
    },

    watch: {
        children: {
            handler(newChildren) {
                // Update filtered children when parent prop changes
                if (!this.hasActiveFilters) {
                    this.filteredChildren = newChildren;
                }
            },
            immediate: true,
        },
    },

    methods: {
        handleFilterByChange(value) {
            this.filterBy = value;
            this.updateCategories();
        },

        handleTagIdsChange(value) {
            this.tagIds = value;
            this.updateCategories();
        },

        handleAttributeIdsChange(value) {
            this.attributeIds = value;
            this.updateCategories();
        },

        updateCategories() {
            debounce(this.fetchFilteredCategories(), 500);
        },

        async fetchFilteredCategories() {
            this.isLoading = true;

            try {
                const params = new URLSearchParams();

                // Add filter_by parameter
                if (this.filterBy !== 'all') {
                    params.append('filter_by', this.filterBy);
                }

                // Add tag_id parameters
                if (this.tagIds.length > 0) {
                    this.tagIds.forEach((id) => {
                        params.append('tag_id[]', id);
                    });
                }

                // Add attribute_id parameters
                if (this.attributeIds.length > 0) {
                    this.attributeIds.forEach((id) => {
                        params.append('attribute_id[]', id);
                    });
                }

                const queryString = params.toString();
                const url = `/categories/${this.parent.slug}${queryString ? `?${queryString}` : ''}`;

                const response = await this.$axios.$get(url);
                this.filteredChildren = response.children || [];
            } catch (error) {
                console.error('Error fetching filtered categories:', error);
                // On error, fall back to original children
                this.filteredChildren = this.children;
            } finally {
                this.isLoading = false;
            }
        },
    },

};
</script>

<style lang="scss" scoped>
</style>
